import {makeObservable, action, computed, observable, override, reaction, IReactionDisposer} from 'mobx';

import {v4 as uuid} from 'uuid';

import _omit from 'lodash/omit';
import _isEqual from 'lodash/isEqual';
import _isFunction from 'lodash/isFunction';
import _isNil from 'lodash/isNil';

import {Analytics} from '@kouch.tv/analytics';
import {EngineModel} from '@kouch.tv/render-engine';
import {TwitchModel} from '@kouch.tv/twitch';

import {SOCKETS, IMessage} from '@kouch.tv/brandis-api';
import {ApplicationChildModel} from '@kouch.tv/brandis-application';

import {version as EXTENSION_VERSION} from '../../../package.json';
import {version as API_VERSION} from '@kouch.tv/brandis-api/package.json';
import {version as APPLICATION_VERSION} from '@kouch.tv/brandis-application/package.json';
import {version as ENGINE_VERSION} from '@kouch.tv/render-engine/package.json';
import {version as TWITCH_HELPER_VERSION} from '@kouch.tv/twitch/package.json';

import {ANALYTICS} from 'src/constants';

import {PlatformModel, IPlatformAnalyticsContext} from 'src/application/platform';

import {
	SECTIONS,
	SECTION_META,
	SECTION_SETTINGS,
	SECTION_VIDEO_OVERLAY,
	TOPICS
} from './constants';

export interface IRuntimeData {
	matches: {
		[matchId: string]: IRuntimeMatch;
	};
}

export interface IRuntimeMatch {
	matchId: string;
	identification: IRuntimeMatchIdentification;
	meta: IRuntimeMatchMeta;
	markup: IRuntimeMatchMarkup;
	settings: IRuntimeMatchSettings;
	session: IRuntimeMatchSession;
}

export interface IRuntimeMatchIdentification {
	organizationId: string;
	campaignId: string;
	integrationId: string;
	mechanicsId: string;
	mechanicsVersion: number;
}

export interface IRuntimeMatchMeta {
	name: string;
}

export interface IRuntimeMatchMarkup {
	overlay: string;
}

export interface IRuntimeMatchSettings {
	restrictions: {
		canParticipate: 'all' | 'trusted' | 'followers' | 'subscribers';
	};
}

export interface IRuntimeMatchSession {
	sessionId: string;
	checksum: {
		calculated: number;
		received: number;
	};
}

export interface IRuntimeCommonData {
	credentials: IRuntimeCommonDataCredentials;
	channel: IRuntimeCommonDataChannel;
}

export interface IRuntimeCommonDataCredentials {
	Twitch: {
		JWT: string;
	};
	ClientID: string;
}

export interface IRuntimeCommonDataChannel {
	ID: string;
	Platform: 'twitch';
}

export interface IRuntimeAnalyticsContext extends IPlatformAnalyticsContext {
	url: string;

	contextVersion: number,

	runtimeExtensionVersion: string;
	runtimeApiVersion: string;
	runtimeApplicationVersion: string;
	runtimeEngineVersion: string;
	runtimeTwitchHelperVersion: string;
}

export interface IRuntimeState {
	isVisible: boolean;
}

export interface IRuntimeMessage {
	id: string;
	title: string;
	text: string;
	actions?: {
		label: string;
		onPress: () => void;
	}[],
	timeout?: number;
	shouldSkip?: (state: IRuntimeState) => boolean;
}

export class RuntimeModel extends ApplicationChildModel<IRuntimeData> {
	@override
	protected $data: IRuntimeData = {
		matches: {},
	};

	@observable
	protected $matchIds: string[] = [];

	@observable
	protected $isAPIHandshakeSucceeded = false;

	@observable
	protected $isAPIIntroduceSucceeded = false;

	@observable
	protected readonly $runtimes: Map<string, {
		engine: EngineModel,
		state: IRuntimeState,
		messages: IRuntimeMessage[],
		reactionDisposers: Set<IReactionDisposer>
	}> = new Map();

	@observable
	protected readonly $connectionTimestamp = {
		[SOCKETS.CONNECTION.BRO]: 0,
		[SOCKETS.CONNECTION.CMD]: 0,
	};

	@observable
	protected $runtimeMessagesTimeoutIds: Map<string, number> = new Map();

	@observable
	protected readonly $activityIndicationCallback: {incoming: Set<() => void>; outgoing: Set<() => void>;} = {
		incoming: new Set(),
		outgoing: new Set(),
	}

	@observable
	public readonly analytics = new Analytics();

	@computed
	public get platform(): PlatformModel {
		return this.application.getModel('platform') as PlatformModel;
	}

	@computed
	public get twitch(): TwitchModel {
		return this.platform.twitch;
	}

	@computed
	public get analyticsContext(): IRuntimeAnalyticsContext {
		return {
			...this.platform.analyticsContext,

			url: window.location.href,

			contextVersion: ANALYTICS.CONTEXT_VERSION,

			runtimeExtensionVersion: EXTENSION_VERSION,
			runtimeApiVersion: API_VERSION,
			runtimeApplicationVersion: APPLICATION_VERSION,
			runtimeEngineVersion: ENGINE_VERSION,
			runtimeTwitchHelperVersion: TWITCH_HELPER_VERSION,
		};
	}

	@computed
	public get runtimes(): {
		matchId: string;
		engine: EngineModel;
		meta: IRuntimeMatchMeta;
		state: IRuntimeState;
		messages: IRuntimeMessage[];
	}[] {
		return this.$matchIds.map(matchId => {
			const runtime = this.$runtimes.get(matchId);

			return {
				matchId: matchId,
				engine: runtime?.engine,
				meta: this.getData(['matches', matchId, 'meta']),
				state: runtime?.state,
				messages: runtime?.messages || [],
			};
		});
	}

	@computed
	public get commonData(): IRuntimeCommonData {
		return {
			credentials: {
				Twitch: {
					JWT: this.twitch.user.tokenPrimary,
				},
				ClientID: this.twitch.api.clientId,
			},
			channel: {
				ID: this.twitch.channel.id,
				Platform: 'twitch',
			}
		};
	}

	@computed
	public get isReadyToShow(): boolean {
		return (
			this.isInitialized &&
			this.twitch.isInitialized &&
			this.$isAPIHandshakeSucceeded &&
			this.$isAPIIntroduceSucceeded
		);
	}

	constructor() {
		super();
		makeObservable(this);
	}

	@override
	public initialize(): this {
		this.$initializeAPIConnectionTimestampWatchers();
		this.$initializeViewerAnalytics();

		this.reaction(
			() => (
				this.twitch.isInitialized
					? {...this.$connectionTimestamp}
					: Object.keys(this.$connectionTimestamp).reduce((result, key) => ({
						...result,
						[key]: 0,
					}), {})
			),
			(currConnectionTimestamp, prevConnectionTimestamp) => {
				for (const [name, currTimestamp] of Object.entries(currConnectionTimestamp)) {
					const prevTimestamp = prevConnectionTimestamp?.[name] || 0;

					if (currTimestamp !== prevTimestamp) {
						const isReconnect = prevTimestamp !== 0;

						this.analytics.track({
							eid: uuid(),
							event: (
								isReconnect
									? ANALYTICS.EVENT.CONNECTION.RECONNECT
									: ANALYTICS.EVENT.CONNECTION.ESTABLISH
							),
							category: ANALYTICS.CATEGORY.INFRASTRUCTURE,
							timestamp: currTimestamp as number,
							connection: name,
							...(
								isReconnect
									? {
										attempt: this.api.socket.getConnection(name)?.attempt,
										lifetime: (currTimestamp as number)  - prevTimestamp,
									}
									: {}
							),
							...this.analyticsContext,
						});
					}
				}
			},
			{
				fireImmediately: true,
			}
		);

		reaction(
			() => this.twitch.isInitialized,
			(currIsInitialized, prevIsInitialized, r) => {
				if (!currIsInitialized) {
					return;
				}

				this.$initializeAPIConnectionBro();
				this.$initializeAPIConnectionCmd();

				super.initialize();

				r.dispose();
			},
			{
				fireImmediately: true,
			}
		);

		return this;
	}

	@override
	public destroy(): void {
		for(const timeoutId of this.$runtimeMessagesTimeoutIds.values()) {
			window.clearTimeout(timeoutId);
		}

		this.$runtimeMessagesTimeoutIds.clear();

		super.destroy();
	}

	@action
	protected $initializeAPIConnectionTimestampWatchers(): void {
		this.api.socket.getConnection(SOCKETS.CONNECTION.BRO)
			.addConnectListener(action(() => {
				this.$connectionTimestamp[SOCKETS.CONNECTION.BRO] = Date.now();
			}))
			.addReconnectListener(action(() => {
				this.$connectionTimestamp[SOCKETS.CONNECTION.BRO] = Date.now();
			}));

		this.api.socket.getConnection(SOCKETS.CONNECTION.CMD)
			.addConnectListener(action(() => {
				this.$connectionTimestamp[SOCKETS.CONNECTION.CMD] = Date.now();
			}))
			.addReconnectListener(action(() => {
				this.$connectionTimestamp[SOCKETS.CONNECTION.CMD] = Date.now();
			}));
	}

	@action
	protected $initializeViewerAnalytics(): void {
		this.reaction(
			() => this.twitch.user.isFollower,
			(currIsFollower, prevIsFollower) => {
				if (_isNil(prevIsFollower) || currIsFollower === prevIsFollower) {
					return;
				}

				this.analytics.track({
					eid: uuid(),
					event: ANALYTICS.EVENT.CHANNEL.FOLLOW_CHANGE,
					category: ANALYTICS.CATEGORY.CHANNEL,
					timestamp: Date.now(),
					...this.analyticsContext,
				});
			},
			{
				fireImmediately: true,
				delay: 1000,
			}
		);

		this.reaction(
			() => this.twitch.user.isSubscriber,
			(currIsSubscriber, prevIsSubscriber) => {
				if (_isNil(prevIsSubscriber) || currIsSubscriber === prevIsSubscriber) {
					return;
				}

				this.analytics.track({
					eid: uuid(),
					event: ANALYTICS.EVENT.CHANNEL.SUBSCRIBE_CHANGE,
					category: ANALYTICS.CATEGORY.CHANNEL,
					timestamp: Date.now(),
					...this.analyticsContext,
				});
			},
			{
				fireImmediately: true,
				delay: 1000,
			}
		);
	}

	@action
	protected $initializeAPIConnectionBro(): void {
		this.reaction(
			() => this.twitch.isInitialized && this.twitch.channel.id,
			(currChannelId, prevChannelId, reaction) => {
				if (!currChannelId) {
					return;
				}

				const handshake = () => {
					this
						.fireActivityIndication('outgoing')
						.api
						.socket
						.send(SOCKETS.CONNECTION.BRO, {
							Command: 'SubscribeRequest',
							Data: {
								Channel: {
									id: currChannelId,
									platform: 'twitch',
								},
								Topics: TOPICS,
							}
						});
				};

				const introduce = () => {
					this
						.fireActivityIndication('outgoing')
						.api
						.socket
						.send(SOCKETS.CONNECTION.BRO, {
							Command: 'Introduce',
							Data: {
								TwitchID: this.twitch.user.id,
							},
						});
				};

				this.api.socket
					.addMessageListener(SOCKETS.CONNECTION.BRO, this.$commonMessageListener);

				this.api.socket.getConnection(SOCKETS.CONNECTION.BRO)
					.addReconnectListener(() => {
						handshake();
						introduce();
					});

				handshake();
				introduce();

				reaction.dispose();
			},
			{
				fireImmediately: true,
			}
		);
	}

	@action
	protected $initializeAPIConnectionCmd(): void {
		this.reaction(
			() => (Object.values(this.getData('matches')).map(match => ({
				mechanicsId: match.identification.mechanicsId,
				mechanicsVersion: match.identification.mechanicsVersion,
			}))),
			(currMechanics, prevMechanics) => {
				if (_isEqual(currMechanics, prevMechanics)) {
					return;
				}

				for (const currMechanicsItem of currMechanics) {
					const prevMechanicsItem = prevMechanics.find(candidate => (
						candidate.mechanicsId === currMechanicsItem.mechanicsId
					));

					if (
						!!prevMechanicsItem &&
						prevMechanicsItem.mechanicsVersion === currMechanicsItem.mechanicsVersion
					) {
						continue;
					}

					this
						.fireActivityIndication('outgoing')
						.api.socket
						.addMessageFormatter(
							SOCKETS.CONNECTION.CMD,
							(outgoingMessage) => ({
								...outgoingMessage,
								Credentials: this.commonData.credentials
							})
						)
						.request(
							SOCKETS.CONNECTION.CMD,
							{
								Topic: 'mechanics_config',
								Data: {
									Command: 'Config',
									Data: {
										MechanicsID: currMechanicsItem.mechanicsId,
										Version: currMechanicsItem.mechanicsVersion,
										Sections: SECTIONS,
									},
								},
							}
						)
						.then(response => {
							this.fireActivityIndication('incoming');

							const matches = Object.values(this.getData('matches')).filter(match => {
								return (
									match.identification.mechanicsId === currMechanicsItem.mechanicsId &&
									match.identification.mechanicsVersion === currMechanicsItem.mechanicsVersion
								);
							});

							this.setData({
								matches: {
									...this.getData('matches'),
									...matches.reduce((result, match) => {
										const storedMatch = this.getData(['matches', match.matchId], {
											identification: {},
											meta: {},
											markup: {},
											settings: {},
										});

										const list = {
											...result,
											[match.matchId]: {
												...storedMatch,
												identification: {
													...storedMatch.identification,
													mechanicsVersion: response?.Data?.Data?.Version || 0,
												},
												meta: {
													...storedMatch.meta,
													...JSON.parse(
														response?.Data?.Data?.Sections?.[SECTION_META] || '{}'
													),
												},
												markup: {
													...storedMatch?.markup || {},
													overlay: response?.Data?.Data?.Sections?.[SECTION_VIDEO_OVERLAY] || '',
												},
												settings: {
													...storedMatch.settings,
													...JSON.parse(
														response?.Data?.Data?.Sections?.[SECTION_SETTINGS] || '{}'
													)
												},
											}
										};

										// Add default name if nothing is passed in metadata
										list[match.matchId].meta.name = (
											!!list[match.matchId].meta.name
												? list[match.matchId].meta.name
												: this.application.getModel('intl').getMessage(
													`Runtime.Interactive.DefaultName.${Math.round(Math.random() * 9)}`
												)
										);

										return list;
									}, {})
								}
							});
						})
						.catch(error => {
							console.error(error);
						});
				}
			},
			{
				fireImmediately: true,
			}
		);
	}

	@action // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
	protected $commonMessageListener = (message) => {
		this.fireActivityIndication('incoming');

		if (message?.Command === 'SubscribeRequest') {
			this.$isAPIHandshakeSucceeded = message?.Data?.Topics.every(topic => topic?.State === 'SUCCESS');

			return;
		}

		if (message?.Data?.Command === 'Introduce') {
			this.$isAPIIntroduceSucceeded = message?.Data?.Data?.Result === 'OK';

			return;
		}

		const matchId = message?.Data?.MatchID;

		if (!matchId) {
			return;
		}

		const match: IRuntimeMatch = this.getData(['matches', matchId], {
			matchId: matchId,
			identification: {
				organizationId: (
					message?.Data?.Prototype?.OrganizationID ||
					this.getData(['matches', matchId, 'identification', 'organizationId'], '')
				),
				campaignId: (
					message?.Data?.Prototype?.CampaignID ||
					this.getData(['matches', matchId, 'identification', 'campaignId'], '')
				),
				integrationId: (
					message?.Data?.Prototype?.IntegrationID ||
					this.getData(['matches', matchId, 'identification', 'integrationId'], '')
				),
				mechanicsId: (
					message?.Data?.Prototype?.MechanicsID ||
					this.getData(['matches', matchId, 'identification', 'mechanicsId'], '')
				),
				mechanicsVersion: (
					message?.Data?.Prototype?.MechanicsVersion ||
					this.getData(['matches', matchId, 'identification', 'mechanicsVersion'], 0)
				),
			},
			meta: this.getData(['matches', matchId, 'meta'], {
				name: '',
				template: '',
			}),
			markup: this.getData(['matches', matchId, 'markup'], {
				overlay: '',
			}),
			settings: this.getData(['matches', matchId, 'settings'], {
				restrictions: {
					canParticipate: 'all'
				},
			}),
			session: this.getData(['matches', matchId, 'session'], {
				sessionId: uuid(),
				checksum: {
					calculated: 0,
					received: 0,
				},
			})
		});

		if (!!message.Data.Leaving) {
			this.$removeMatch(match);
			return;
		}

		if (!this.$matchIds.includes(matchId)) {
			this.$addMatch(match);
		}

		this.$runtimes.get(matchId)
			.engine.bus
			.fire('drop', message);
	}

	@action
	protected $addMatch(match: IRuntimeMatch): this {
		this.setData({
			matches: {
				...this.getData('matches'),
				[match.matchId]: match,
			}
		});

		const engine = new EngineModel();

		engine
			.manager
			.setOptions({
				isSceneChangeAnimated: true,
			})
			.engine
			.analytics
			.addTransport('primary', this.analytics.track.bind(this.analytics))
			.setContext({
				...this.analyticsContext,
				...match.identification,
				matchId: match.matchId,
			})
			.engine
			.bus
			.addSubscription('rise', (message) => {
				if (message.__type !== 'overlay-command') {
					return;
				}

				switch (message.action) {
					case 'updateState':
						this.updateRuntimeState(match.matchId, message.payload || {});

						break;

					case 'addMessage':
						this.addRuntimeMessage(match.matchId, message.payload || {});

						break;

					case 'removeMessage':
						this.removeRuntimeMessage(match.matchId, message.paylaod || '');

						break;
				}
			})
			.addSubscription('rise', (message) => {
				if (!!message.__type && message.__type !== 'socket-request') {
					return;
				}

				new Promise<void>((resolve, reject) => {
					const canParticipate = (
						message?.['__settings-restrictions-canParticipate'] ||
						this.getData(['matches', match.matchId, 'settings', 'restrictions', 'canParticipate'], 'all') ||
						'all'
					);

					switch (canParticipate) {
						case 'trusted':
							if (!this.twitch.user.isTrusted) {
								return reject([
									'require-share-id'
								]);
							}

							return resolve();

						case 'followers':
							if (!this.twitch.user.isFollower) {
								return reject([
									!this.twitch.user.isTrusted && 'require-share-id',
									'require-follow'
								].filter(Boolean));
							}

							return resolve();

						case 'subscribers':
							if (!this.twitch.user.isSubscriber) {
								return reject([
									!this.twitch.user.isTrusted && 'require-share-id',
									'require-subscribe'
								].filter(Boolean));
							}

							return resolve();

						case 'all':
							return resolve();
					}
				})
					.catch(error => {
						if (error instanceof Error) {
							throw error;
						}

						if (!Array.isArray(error)) {
							throw new Error('Unknown error while restrictions check.');
						}

						const restriction = error.shift();
						const intl = this.application.getModel('intl');

						return new Promise<void>((resolve) => {
							const messageId = uuid();

							switch (restriction) {
								case 'require-share-id':
									this.reaction(
										() => this.twitch.user.isTrusted,
										(isTrusted, _, r) => {
											if (!isTrusted) {
												return;
											}

											resolve();
											this.removeRuntimeMessage(match.matchId, messageId);

											r.dispose();
										}
									);

									this.twitch.shareId();

									this.addRuntimeMessage(match.matchId, {
										id: messageId,
										title: intl.getMessage('Runtime.Guard.ShareId.Title'),
										text: `${intl.getMessage('Runtime.Guard.ShareId.Message')}\n\n${intl.getMessage('Runtime.Guard.Disclaimer')}`,
										actions: [
											{
												label: intl.getMessage('Common.Dismiss'),
												onPress: () => {
													this.removeRuntimeMessage(match.matchId, messageId);
												}
											}
										],
									});

									break;

								case 'require-follow':
									this.reaction(
										() => this.twitch.user.isFollower,
										(isFollower, _, r) => {
											if (!isFollower) {
												return;
											}

											resolve();
											this.removeRuntimeMessage(match.matchId, messageId);

											r.dispose();
										}
									);

									this.twitch.channel.follow();

									this.addRuntimeMessage(match.matchId, {
										id: messageId,
										title: intl.getMessage('Runtime.Guard.Follow.Title'),
										text: `${intl.getMessage('Runtime.Guard.Follow.Message')}\n\n${intl.getMessage('Runtime.Guard.Disclaimer')}`,
										actions: [
											{
												label: intl.getMessage('Common.Dismiss'),
												onPress: () => {
													this.removeRuntimeMessage(match.matchId, messageId);
												}
											}
										],
									});

									break;

								case 'require-subscribe':
									this.reaction(
										() => this.twitch.user.isSubscriber,
										(isSubscriber, _, r) => {
											if (!isSubscriber) {
												return;
											}

											resolve();
											this.removeRuntimeMessage(match.matchId, messageId);

											r.dispose();
										}
									);

									this.twitch.channel.subscribe();

									this.addRuntimeMessage(match.matchId, {
										id: messageId,
										title: intl.getMessage('Runtime.Guard.Subscribe.Title'),
										text: `${intl.getMessage('Runtime.Guard.Subscribe.Message')}\n\n${intl.getMessage('Runtime.Guard.Disclaimer')}`,
										actions: [
											{
												label: intl.getMessage('Common.Dismiss'),
												onPress: () => {
													this.removeRuntimeMessage(match.matchId, messageId);
												}
											}
										],
									});

									break;
							}
						});
					})
					.then(() => {
						this
							.fireActivityIndication('outgoing')
							.api.socket.request(
								SOCKETS.CONNECTION.CMD,
								{
									..._omit(message, ['__type']),
									Data: {
										...(message?.Data as IMessage || {}),
										Data: {
											...((message?.Data as IMessage)?.Data as IMessage || {}),
											MatchID: match.matchId,
										},
									}
								}
							)
							.then((message) => {
								this
									.fireActivityIndication('incoming')
									.updateData(
										['matches', match.matchId, 'session', 'checksum', 'received'],
										(
											message?.Data?.Data?.Profile?.SessionSequence ||
											this.getData(
												['matches', match.matchId, 'session', 'checksum', 'received'],
												0
											)
										)
									);

								engine.bus
									.fire('drop', message);
							});
					});
			});

		const primaryReactionDisposer = this.reaction(
			() => (this.twitch.isInitialized && {
				token: this.twitch.user.tokenPrimary,
				clientId: this.twitch.api.clientId,
			}),
			(credentials) => {
				if ([credentials?.token, credentials?.clientId].some(value => !value)) {
					return;
				}

				const engine = this.$runtimes.get(match.matchId).engine;

				this
					.fireActivityIndication('outgoing')
					.api.socket.request(
						SOCKETS.CONNECTION.CMD,
						{
							Topic: 'reactor_profile',
							Data: {
								Command: 'GetProfile',
								Data: {
									MatchID: match.matchId,
									ChannelID: this.commonData.channel,
									SessionId: this.getData(['matches', match.matchId, 'session', 'sessionId'])
								}
							}
						}
					)
					.then((message) => {
						this
							.fireActivityIndication('incoming')
							.updateData(
								['matches', match.matchId, 'session', 'checksum', 'received'],
								(
									message?.Data?.Data?.Profile?.SessionSequence ||
									this.getData(
										['matches', match.matchId, 'session', 'checksum', 'received'],
										0
									)
								)
							);

						engine.bus
							.fire('drop', message);
					});

				engine
					.initialize();
			},
			{fireImmediately: true}
		);

		this.$runtimes.set(match.matchId, {
			engine: engine,
			state: {
				isVisible: true,
			},
			messages: [],
			reactionDisposers: new Set([primaryReactionDisposer]),
		});

		this.$matchIds.push(match.matchId);

		return this;
	}

	@action
	protected $removeMatch(match: IRuntimeMatch): this {
		if (!this.$matchIds.includes(match.matchId)) {
			return this;
		}

		this.setData({
			matches: _omit(this.getData('matches'), [match.matchId]),
		});

		this.$matchIds = this.$matchIds.filter(candidateId => candidateId !== match.matchId);

		const {reactionDisposers} = this.$runtimes.get(match.matchId);

		for (const reactionDisposer of reactionDisposers) {
			if (!_isFunction(reactionDisposer)) {
				continue;
			}

			reactionDisposer();
		}

		this.$runtimes.delete(match.matchId);

		return this;
	}

	public fireActivityIndication(direction: 'incoming' | 'outgoing'): this {
		for (const callback of this.$activityIndicationCallback[direction]) {
			if (!_isFunction(callback)) {
				continue;
			}

			callback();
		}

		return this;
	}

	@action
	public addActivityIndicationCallback(direction: 'incoming' | 'outgoing', callback: (() => void)): this {
		this.$activityIndicationCallback[direction].add(callback);

		return this;
	}

	@action
	public removeActivityIndicationCallback(direction: 'incoming' | 'outgoing', callback: (() => void)): this {
		this.$activityIndicationCallback[direction].delete(callback);

		return this;
	}

	@action
	public updateRuntimeState(matchId: string, state: Partial<IRuntimeState>): this {
		if (!this.$runtimes.has(matchId)) {
			return this;
		}

		const runtime = this.$runtimes.get(matchId);

		this.$runtimes.set(matchId, {
			...runtime,
			state: {
				...runtime.state,
				...state
			},
		});

		return this;
	}

	@action
	public addRuntimeMessage(matchId: string, message: IRuntimeMessage): this {
		if (!this.$runtimes.has(matchId)) {
			return this;
		}

		const runtime = this.$runtimes.get(matchId);

		if (_isFunction(message.shouldSkip) && message.shouldSkip(runtime.state)) {
			return this;
		}

		this.$runtimes.set(matchId, {
			...runtime,
			messages: [
				message,
				...runtime.messages,
			],
		});

		if (Number.isFinite(message.timeout) && message.timeout > 0) {
			this.$runtimeMessagesTimeoutIds.set(`${matchId}-${message.id}`, window.setTimeout(() => {
				this.removeRuntimeMessage(matchId, message.id);
				this.$runtimeMessagesTimeoutIds.delete(`${matchId}-${message.id}`);
			}, message.timeout));
		}

		return this;
	}

	@action
	public removeRuntimeMessage(matchId: string, messageId: IRuntimeMessage['id']): this {
		if (!this.$runtimes.has(matchId)) {
			return this;
		}

		const runtime = this.$runtimes.get(matchId);

		this.$runtimes.set(matchId, {
			...runtime,
			messages: runtime.messages.filter(message => message.id !== messageId),
		});

		return this;
	}
}
